import { useEffect, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, Loader2, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import ProposalSheet from "./ProposalSheet";
import ProposalStatusBadge from "./ProposalStatusBadge";

function formatBRL(raw: string | number | null | undefined, currency = "BRL"): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency });
}

/**
 * Diálogo "Nova proposta": escolhe o modelo, preenche os campos manuais e
 * gera. Os campos de IA são escritos no servidor; o vendedor revisa depois na
 * ficha da proposta.
 */
export function NewProposalDialog({
  dealId,
  open,
  onOpenChange,
  onCreated,
}: {
  dealId: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (proposalId: number) => void;
}) {
  const utils = trpc.useUtils();
  const templates = trpc.crm.proposalTemplates.list.useQuery(undefined, { enabled: open });
  const [templateId, setTemplateId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [useAi, setUseAi] = useState(true);

  const template = trpc.crm.proposalTemplates.get.useQuery(
    { id: Number(templateId) },
    { enabled: open && !!templateId },
  );
  const manualVars = (template.data?.effectiveVariables ?? []).filter((v) => v.kind === "manual");
  const aiVars = (template.data?.effectiveVariables ?? []).filter((v) => v.kind === "ai");

  useEffect(() => {
    if (!open) return;
    const list = templates.data ?? [];
    if (!templateId && list.length > 0) setTemplateId(String(list[0].id));
  }, [open, templates.data, templateId]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const v of manualVars) next[v.key] = v.defaultValue ?? "";
    setValues(next);
  }, [template.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = trpc.crm.proposals.generate.useMutation({
    onSuccess: (row) => {
      toast.success(`Proposta ${row.number} gerada`);
      utils.crm.proposals.list.invalidate();
      utils.crm.proposals.counts.invalidate();
      utils.crm.deals.listActivities.invalidate();
      onOpenChange(false);
      setTitle("");
      onCreated(row.id);
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao gerar proposta")),
  });

  const noTemplates = templates.isSuccess && (templates.data?.length ?? 0) === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !generate.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Nova proposta</DialogTitle>
          <DialogDescription>
            O sistema preenche os dados da negociação, do contato e dos produtos; a IA escreve os textos; você revisa antes de mandar ao gestor.
          </DialogDescription>
        </DialogHeader>

        {noTemplates ? (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Nenhum modelo de proposta cadastrado ainda.{" "}
            <Link href="/crm/proposals?tab=templates" className="underline font-medium text-foreground">Criar o primeiro modelo</Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Modelo</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger><SelectValue placeholder="Escolha um modelo" /></SelectTrigger>
                <SelectContent>
                  {(templates.data ?? []).map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {template.data?.description && (
                <p className="text-xs text-muted-foreground mt-1">{template.data.description}</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Título (opcional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={template.data?.name ?? "Proposta comercial"} maxLength={300} />
            </div>
            {manualVars.map((v) => (
              <div key={v.key}>
                <Label className="text-xs">{v.label}</Label>
                {v.multiline !== false ? (
                  <Textarea rows={3} value={values[v.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))} />
                ) : (
                  <Input value={values[v.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))} />
                )}
              </div>
            ))}
            {aiVars.length > 0 && (
              <label className="flex items-start gap-2 text-sm cursor-pointer rounded-md border p-3">
                <Checkbox checked={useAi} onCheckedChange={(c) => setUseAi(c === true)} className="mt-0.5" />
                <span>
                  <span className="inline-flex items-center gap-1 font-medium"><Sparkles className="h-3.5 w-3.5 text-primary" /> Escrever com IA</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {aiVars.map((v) => v.label).join(", ")} — a partir do histórico da negociação. Você pode editar depois.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generate.isPending}>Cancelar</Button>
          <Button
            disabled={generate.isPending || !templateId || noTemplates}
            onClick={() => generate.mutate({ dealId, templateId: Number(templateId), title: title || undefined, values, useAi })}
          >
            {generate.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
            {generate.isPending ? "Gerando…" : "Gerar proposta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DealProposalsPanel({ dealId }: { dealId: number }) {
  const { can } = usePermissions();
  const canManage = can("crm:proposals_manage");
  const list = trpc.crm.proposals.list.useQuery({ dealId });
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  if (!can("crm:proposals_view")) {
    return <p className="text-sm text-muted-foreground">Você não tem permissão para ver propostas.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Propostas</h3>
          <p className="text-xs text-muted-foreground">Geradas a partir de um modelo, aprovadas pelo gestor e enviadas ao cliente.</p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Nova proposta
          </Button>
        )}
      </div>

      {list.isLoading ? (
        <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>
      ) : (list.data?.length ?? 0) === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Nenhuma proposta nesta negociação ainda.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {list.data!.map((row) => (
            <button
              key={row.proposal.id}
              type="button"
              onClick={() => setOpenId(row.proposal.id)}
              className="w-full text-left rounded-lg border bg-card hover:bg-accent/50 transition-colors px-4 py-3 flex items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{row.proposal.number}</span>
                  <span className="font-medium truncate">{row.proposal.title}</span>
                  <ProposalStatusBadge status={row.proposal.status} />
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {formatBRL(row.proposal.totalValue, row.proposal.currency)} · v{row.proposal.version} · {new Date(row.proposal.updatedAt).toLocaleDateString("pt-BR")}
                  {row.createdBy?.name ? ` · ${row.createdBy.name}` : ""}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}

      <NewProposalDialog dealId={dealId} open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => setOpenId(id)} />
      <ProposalSheet proposalId={openId} open={openId !== null} onOpenChange={(v) => !v && setOpenId(null)} />
    </div>
  );
}
